/**
 * TargetWorkspaceDialog — Phase 2.2B Session 3
 *
 * Three-step workspace: BMR → TDEE → Macros → Save.
 *
 * Calls:
 *   POST /api/nutrition/engine/bmr            (computes one or all 4 formulas server-side)
 *   POST /api/nutrition/targets/suggest        (auto goal-aware macro suggestion)
 *   POST /api/nutrition/targets                (create new target — auto-closes previous)
 *
 * No formulas are computed client-side; TDEE = bmr × activity_factor is a display-only product
 * that mirrors the backend (`compute_tdee`).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, Loader2, Save, Sparkles, Calculator, Target as TargetIcon, Activity } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const BMR_FORMULAS = [
  { id: 'HARRIS_BENEDICT', label: 'Harris-Benedict' },
  { id: 'MIFFLIN_ST_JEOR', label: 'Mifflin-St Jeor' },
  { id: 'CUNNINGHAM', label: 'Cunningham' },
  { id: 'OWEN', label: 'Owen' },
];

const ACTIVITY_LEVELS = [
  { id: 'SEDENTARY', factor: 1.2, label_it: 'Sedentario', label_en: 'Sedentary' },
  { id: 'LIGHT', factor: 1.375, label_it: 'Leggera', label_en: 'Light' },
  { id: 'MODERATE', factor: 1.55, label_it: 'Moderata', label_en: 'Moderate' },
  { id: 'HIGH', factor: 1.725, label_it: 'Alta', label_en: 'High' },
  { id: 'VERY_HIGH', factor: 1.9, label_it: 'Molto alta', label_en: 'Very high' },
];

const GOALS = [
  { id: 'MAINTENANCE', label_it: 'Mantenimento', label_en: 'Maintenance' },
  { id: 'FAT_LOSS', label_it: 'Dimagrimento', label_en: 'Fat loss' },
  { id: 'MUSCLE_GAIN', label_it: 'Aumento massa', label_en: 'Muscle gain' },
  { id: 'PERFORMANCE', label_it: 'Performance', label_en: 'Performance' },
  { id: 'RTP', label_it: 'Return to play', label_en: 'Return to play' },
  { id: 'CUSTOM', label_it: 'Custom', label_en: 'Custom' },
];

const toNum = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));
const fmt1 = (v) => v === null || v === undefined ? '—' : Number(v).toFixed(1);
const fmt0 = (v) => v === null || v === undefined ? '—' : Math.round(Number(v));
const todayIso = () => new Date().toISOString().slice(0, 10);

const computeAge = (birth) => {
  if (!birth) return null;
  try {
    const d = new Date(birth);
    return Math.floor((Date.now() - d.getTime()) / (365.25 * 86400000));
  } catch { return null; }
};

const TargetWorkspaceDialog = ({ open, onClose, athleteId, athlete, latestAnthro, onSaved }) => {
  const { api } = useAuth();
  const { language } = useLanguage();

  // Inputs (prefilled from athlete + latest anthropometry)
  const [sex, setSex] = useState('M');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [leanMass, setLeanMass] = useState('');

  // BMR
  const [bmrResults, setBmrResults] = useState(null); // {HARRIS_BENEDICT: 1750, ...}
  const [bmrFormula, setBmrFormula] = useState('MIFFLIN_ST_JEOR');
  const [computingBmr, setComputingBmr] = useState(false);
  const bmrValue = bmrResults?.[bmrFormula] ?? null;

  // TDEE
  const [activityLevel, setActivityLevel] = useState('MODERATE');

  // Goal + macros
  const [goal, setGoal] = useState('MAINTENANCE');
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [carbs, setCarbs] = useState('');
  const [fats, setFats] = useState('');
  const [suggested, setSuggested] = useState(null);  // audit copy of /suggest output
  const [mode, setMode] = useState('MANUAL'); // AUTO if user used /suggest unchanged; MANUAL otherwise
  const [sourceMeasurementId, setSourceMeasurementId] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [rationale, setRationale] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Prefill on open
  useEffect(() => {
    if (!open) return;
    setSex(athlete?.sex || 'M');
    setAge(computeAge(athlete?.birth_date) ?? '');
    setHeight(athlete?.height_cm ?? '');
    setWeight(latestAnthro?.weight_kg ?? '');
    setLeanMass(latestAnthro?.lean_mass_kg ?? '');
    setBmrResults(null);
    setBmrFormula('MIFFLIN_ST_JEOR');
    setActivityLevel('MODERATE');
    setGoal('MAINTENANCE');
    setCalories(''); setProtein(''); setCarbs(''); setFats('');
    setSuggested(null);
    setSourceMeasurementId(latestAnthro?.id ?? null);
    setMode('MANUAL');
    setRationale('');
    setError(null);
  }, [open, athlete, latestAnthro]);

  const tdee = useMemo(() => {
    if (bmrValue === null) return null;
    const af = ACTIVITY_LEVELS.find((a) => a.id === activityLevel)?.factor || 1.55;
    return Math.round(bmrValue * af);
  }, [bmrValue, activityLevel]);

  // g/kg helpers
  const gpk = (g, w) => {
    const gn = toNum(g); const wn = toNum(w);
    if (gn === null || wn === null || wn === 0) return null;
    return gn / wn;
  };

  if (!open) return null;

  const handleComputeBmr = async () => {
    setError(null);
    const payload = {
      sex,
      age_years: toNum(age),
      weight_kg: toNum(weight),
      height_cm: toNum(height),
    };
    const lm = toNum(leanMass);
    if (lm !== null) payload.lean_mass_kg = lm;
    if (!payload.age_years || !payload.weight_kg || !payload.height_cm) {
      setError(language === 'IT' ? 'Età, peso e altezza sono obbligatori per il calcolo BMR' : 'Age, weight and height are required for BMR calculation');
      return;
    }
    setComputingBmr(true);
    try {
      // No `formula` field → backend returns all 4
      const res = await api.post('/nutrition/engine/bmr', payload);
      const map = {};
      (res.data.results || []).forEach((r) => { map[r.formula] = r.bmr_kcal; });
      setBmrResults(map);
      // Keep current formula if available, else pick first usable one
      if (map[bmrFormula] === undefined) {
        const first = Object.keys(map)[0];
        if (first) setBmrFormula(first);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'BMR calculation failed');
    } finally {
      setComputingBmr(false);
    }
  };

  const handleAutoSuggest = async () => {
    setError(null);
    setSuggesting(true);
    try {
      const res = await api.post('/nutrition/targets/suggest', {
        athlete_id: athleteId,
        goal,
        activity_level: activityLevel,
      });
      const s = res.data.suggested || {};
      setSuggested({ ...s });
      setCalories(s.calories ?? '');
      setProtein(s.protein_g ?? '');
      setCarbs(s.carbs_g ?? '');
      setFats(s.fats_g ?? '');
      setMode('AUTO');
      if (res.data.source_measurement_id) setSourceMeasurementId(res.data.source_measurement_id);
      // Server tells us what BMR + activity factor it used — surface that
      if (res.data.bmr_kcal && bmrResults) {
        // Detect which formula matches what server returned (within 0.1) for display
        const match = Object.entries(bmrResults).find(([, v]) => Math.abs(v - res.data.bmr_kcal) < 0.5);
        if (match) setBmrFormula(match[0]);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Auto-suggest failed');
    } finally {
      setSuggesting(false);
    }
  };

  const handleMacroChange = (setter) => (v) => {
    setter(v);
    setMode('MANUAL');
  };

  const handleSave = async () => {
    setError(null);
    const final = {
      calories: toNum(calories),
      protein_g: toNum(protein),
      carbs_g: toNum(carbs),
      fats_g: toNum(fats),
    };
    if (final.calories === null || final.protein_g === null || final.carbs_g === null || final.fats_g === null) {
      setError(language === 'IT' ? 'Calorie e tutti i macro sono obbligatori' : 'Calories and all macros are required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        athlete_id: athleteId,
        effective_from: todayIso(),
        goal,
        mode,
        final,
      };
      if (suggested && suggested.calories) body.suggested = suggested;
      if (sourceMeasurementId) body.source_measurement_id = sourceMeasurementId;
      if (rationale.trim()) body.rationale = rationale.trim();
      const res = await api.post('/nutrition/targets', body);
      onSaved && onSaved(res.data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map((d) => d.msg).join('; ') : (detail || 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const proteinPerKg = gpk(protein, weight);
  const carbsPerKg = gpk(carbs, weight);
  const fatsPerKg = gpk(fats, weight);
  const activityFactor = ACTIVITY_LEVELS.find((a) => a.id === activityLevel)?.factor;
  const lbl = (o) => language === 'IT' ? o.label_it : o.label_en;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="target-workspace-dialog">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-4xl max-h-[92vh] overflow-y-auto m-4">
        <header className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <TargetIcon className="w-5 h-5 text-cyan-400" />
            <div>
              <h2 className="text-lg font-semibold text-zinc-100">
                {language === 'IT' ? 'Nuovo target nutrizionale' : 'New nutrition target'}
              </h2>
              <p className="text-xs text-zinc-500 mt-0.5">{athlete ? `${athlete.first_name} ${athlete.last_name}` : ''}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="target-dialog-close">
            <X className="w-4 h-4" />
          </Button>
        </header>

        <div className="p-6 space-y-6">
          {/* Step 1 — Inputs */}
          <section className="bg-zinc-950/50 border border-zinc-800 rounded p-4" data-testid="target-inputs-section">
            <h3 className="text-xs uppercase tracking-wider text-zinc-400 mb-3">
              1 · {language === 'IT' ? 'Dati di base' : 'Base inputs'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              <div>
                <label className="text-[10px] uppercase text-zinc-500">{language === 'IT' ? 'Sesso' : 'Sex'}</label>
                <select value={sex} onChange={(e) => setSex(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200"
                  data-testid="target-sex">
                  <option value="M">M</option><option value="F">F</option>
                </select>
              </div>
              <Field testid="target-age" label={language === 'IT' ? 'Età' : 'Age'} value={age} onChange={setAge} suffix="yr" />
              <Field testid="target-weight" label={language === 'IT' ? 'Peso' : 'Weight'} value={weight} onChange={setWeight} suffix="kg" />
              <Field testid="target-height" label={language === 'IT' ? 'Altezza' : 'Height'} value={height} onChange={setHeight} suffix="cm" />
              <Field testid="target-lean" label={language === 'IT' ? 'Massa magra' : 'Lean mass'} value={leanMass} onChange={setLeanMass} suffix="kg" placeholder={language === 'IT' ? 'opzionale' : 'optional'} />
            </div>
          </section>

          {/* Step 2 — BMR */}
          <section className="bg-zinc-950/50 border border-zinc-800 rounded p-4" data-testid="bmr-section">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Calculator className="w-3.5 h-3.5" />
                2 · BMR
              </h3>
              <Button size="sm" variant="outline" onClick={handleComputeBmr} disabled={computingBmr} data-testid="bmr-compute">
                {computingBmr ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Calculator className="w-3.5 h-3.5 mr-1.5" />}
                {language === 'IT' ? 'Calcola BMR' : 'Compute BMR'}
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="bmr-formula-grid">
              {BMR_FORMULAS.map((f) => {
                const v = bmrResults?.[f.id];
                const isSelected = bmrFormula === f.id;
                const disabled = bmrResults && v === undefined;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => !disabled && setBmrFormula(f.id)}
                    disabled={disabled}
                    data-testid={`bmr-formula-${f.id}`}
                    className={`border rounded p-3 text-left transition ${
                      isSelected ? 'border-cyan-500 bg-cyan-500/10' : 'border-zinc-800 hover:border-zinc-600'
                    } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-zinc-400">{f.label}</div>
                    <div className={`text-xl font-bold ${isSelected ? 'text-cyan-300' : 'text-zinc-100'}`}>
                      {v === undefined ? '—' : `${fmt0(v)} kcal`}
                    </div>
                    {f.id === 'CUNNINGHAM' && !leanMass && (
                      <div className="text-[10px] text-amber-400/80 mt-1">{language === 'IT' ? 'Richiede FFM' : 'Needs FFM'}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Step 3 — TDEE */}
          <section className="bg-zinc-950/50 border border-zinc-800 rounded p-4" data-testid="tdee-section">
            <h3 className="text-xs uppercase tracking-wider text-zinc-400 mb-3 flex items-center gap-2">
              <Activity className="w-3.5 h-3.5" /> 3 · TDEE
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
              {ACTIVITY_LEVELS.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setActivityLevel(a.id)}
                  data-testid={`activity-${a.id}`}
                  className={`border rounded p-2 text-xs transition ${
                    activityLevel === a.id ? 'border-cyan-500 bg-cyan-500/10 text-cyan-200' : 'border-zinc-800 hover:border-zinc-600 text-zinc-300'
                  }`}
                >
                  <div className="font-medium">{lbl(a)}</div>
                  <div className="text-zinc-500">×{a.factor}</div>
                </button>
              ))}
            </div>
            <div className="bg-zinc-900 rounded p-3 flex items-center justify-between text-sm">
              <span className="text-zinc-400">
                {language === 'IT' ? 'Calcolo' : 'Calculation'}:{' '}
                <span className="text-zinc-200">{fmt0(bmrValue)} × {activityFactor}</span>
              </span>
              <span className="font-bold text-lg text-cyan-300" data-testid="tdee-value">
                {tdee === null ? '—' : `${tdee} kcal`}
              </span>
            </div>
          </section>

          {/* Step 4 — Goal + Macros */}
          <section className="bg-zinc-950/50 border border-zinc-800 rounded p-4" data-testid="macros-section">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-xs uppercase tracking-wider text-zinc-400">
                4 · {language === 'IT' ? 'Obiettivo & Macronutrienti' : 'Goal & macros'}
              </h3>
              <Button
                size="sm" variant="outline" onClick={handleAutoSuggest}
                disabled={suggesting || !weight}
                data-testid="target-auto-suggest"
                className="border-emerald-700 text-emerald-300 hover:bg-emerald-500/10"
              >
                {suggesting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                {language === 'IT' ? 'Genera automaticamente' : 'Generate automatically'}
              </Button>
            </div>

            <div className="mb-3">
              <label className="text-[10px] uppercase text-zinc-500">{language === 'IT' ? 'Obiettivo' : 'Goal'}</label>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="goal-selector">
                {GOALS.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setGoal(g.id)}
                    data-testid={`goal-${g.id}`}
                    className={`px-2.5 py-1 text-xs rounded border transition ${
                      goal === g.id ? 'border-cyan-500 bg-cyan-500/10 text-cyan-200' : 'border-zinc-800 hover:border-zinc-600 text-zinc-300'
                    }`}
                  >{lbl(g)}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <MacroField testid="macro-calories" label={language === 'IT' ? 'Calorie' : 'Calories'} value={calories} onChange={handleMacroChange(setCalories)} suffix="kcal" />
              <MacroField testid="macro-protein" label={language === 'IT' ? 'Proteine' : 'Protein'} value={protein} onChange={handleMacroChange(setProtein)} suffix="g" perKg={proteinPerKg} />
              <MacroField testid="macro-carbs" label={language === 'IT' ? 'Carboidrati' : 'Carbs'} value={carbs} onChange={handleMacroChange(setCarbs)} suffix="g" perKg={carbsPerKg} />
              <MacroField testid="macro-fats" label={language === 'IT' ? 'Grassi' : 'Fats'} value={fats} onChange={handleMacroChange(setFats)} suffix="g" perKg={fatsPerKg} />
            </div>

            <div className="mt-3 flex items-center gap-2 text-xs">
              <Badge className={mode === 'AUTO' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : 'bg-zinc-700 text-zinc-200 border-zinc-600'}
                data-testid="target-mode-badge">
                {language === 'IT' ? 'Sorgente' : 'Source'}: {mode}
              </Badge>
              {suggested && mode === 'MANUAL' && (
                <span className="text-zinc-500">
                  {language === 'IT' ? 'I valori suggeriti rimangono salvati come audit.' : 'Auto-suggested values are retained for audit.'}
                </span>
              )}
            </div>

            <div className="mt-3">
              <label className="text-[10px] uppercase text-zinc-500">{language === 'IT' ? 'Note / razionale' : 'Notes / rationale'}</label>
              <textarea
                value={rationale} onChange={(e) => setRationale(e.target.value)} rows={2}
                placeholder={language === 'IT' ? 'es. fase di carico pre-stagione, surplus 250 kcal' : 'e.g. pre-season loading phase, 250 kcal surplus'}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-cyan-600 resize-none"
                data-testid="target-rationale"
              />
            </div>
          </section>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="target-dialog-error">
              {error}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-zinc-900 border-t border-zinc-800 px-6 py-3 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="target-dialog-cancel">
            {language === 'IT' ? 'Annulla' : 'Cancel'}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="target-dialog-save"
            className="bg-cyan-600 hover:bg-cyan-500 text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {language === 'IT' ? 'Salva target' : 'Save target'}
          </Button>
        </footer>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, suffix, placeholder, testid }) => (
  <div>
    <label className="text-[10px] uppercase text-zinc-500">{label}</label>
    <div className="relative">
      <input
        type="number" step="0.1" value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-cyan-600 pr-10"
        data-testid={testid}
      />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500 pointer-events-none">{suffix}</span>
    </div>
  </div>
);

const MacroField = ({ label, value, onChange, suffix, perKg, testid }) => (
  <div>
    <label className="text-[10px] uppercase text-zinc-500">{label}</label>
    <div className="relative">
      <input
        type="number" step="0.1" value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-base font-medium text-zinc-100 focus:outline-none focus:border-cyan-600 pr-10"
        data-testid={testid}
      />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500 pointer-events-none">{suffix}</span>
    </div>
    {perKg !== undefined && perKg !== null && (
      <div className="text-[10px] text-zinc-500 mt-0.5" data-testid={`${testid}-per-kg`}>
        {fmt1(perKg)} g/kg
      </div>
    )}
  </div>
);

export default TargetWorkspaceDialog;
